/**
 * Google Fonts catalog + lazy loader — Tier 2 Group B.
 *
 * Static catalog of ~60 popular Google Fonts plus a handful of
 * system fonts kept for offline / fallback. The catalog lives here
 * (not as a remote fetch from the Google Fonts API) so picker
 * results are stable, the bundle stays predictable, and the picker
 * works without network at all (the font face only loads on
 * selection — until then the row renders via the CSS fallback
 * stack).
 *
 * Loading model:
 *  - Each entry carries a `preview` CSS font-family string. The
 *    fallback chain renders even before the @font-face arrives,
 *    so picker rows are never blank.
 *  - When a user selects a Google font, `ensureGoogleFontLoaded`
 *    injects a `<link rel="stylesheet">` to fonts.googleapis.com.
 *    The link is cached by family so re-selection is free.
 *  - System fonts (Arial, Helvetica, etc.) never trigger a network
 *    fetch — they have no `googleFamily` and short-circuit the
 *    loader.
 */

export type FontCategory = 'sans' | 'serif' | 'display' | 'mono' | 'handwriting' | 'system';

export interface FontEntry {
  /** The value persisted to `TextSettings.fontFamily` AND the
   *  family name shipped to Fabric IText. Must match a CSS font-
   *  family name (Google Fonts API returns this name). */
  value: string;
  /** Display label shown in the picker. */
  label: string;
  /** CSS `font-family` string used to render the picker row (and
   *  the IText fallback). Includes generic fallbacks so unloaded
   *  fonts still render legibly. */
  preview: string;
  /** Category for the picker chip filter. `system` fonts never
   *  fetch from Google Fonts. */
  category: FontCategory;
  /** When set, `ensureGoogleFontLoaded(value)` fetches Google Fonts
   *  CSS for this family. Optional weights/styles default to
   *  `wght@400;700` + italic. Family name must match the Google
   *  Fonts catalog spelling (case + spaces — replaced with `+` in
   *  the URL). */
  googleFamily?: string;
}

/** Catalog. Order within a category is roughly popularity-based;
 *  the picker preserves it. */
export const FONT_CATALOG: readonly FontEntry[] = [
  // System fonts — keep at the top so they remain reachable in the
  // All filter on slow networks. These match the pre-Tier 2 list.
  { value: 'Arial', label: 'Arial', preview: 'Arial, Helvetica, sans-serif', category: 'system' },
  {
    value: 'Helvetica',
    label: 'Helvetica',
    preview: 'Helvetica, Arial, sans-serif',
    category: 'system',
  },
  {
    value: 'Georgia',
    label: 'Georgia',
    preview: 'Georgia, "Times New Roman", serif',
    category: 'system',
  },
  {
    value: 'Times New Roman',
    label: 'Times New Roman',
    preview: '"Times New Roman", Times, serif',
    category: 'system',
  },
  {
    value: 'Courier New',
    label: 'Courier New',
    preview: '"Courier New", Courier, monospace',
    category: 'system',
  },
  {
    value: 'Verdana',
    label: 'Verdana',
    preview: 'Verdana, Geneva, sans-serif',
    category: 'system',
  },

  // Sans-serif
  ...sans('Roboto', 'Roboto'),
  ...sans('Open Sans', 'Open Sans'),
  ...sans('Lato', 'Lato'),
  ...sans('Montserrat', 'Montserrat'),
  ...sans('Inter', 'Inter'),
  ...sans('Poppins', 'Poppins'),
  ...sans('Nunito', 'Nunito'),
  ...sans('Raleway', 'Raleway'),
  ...sans('Oswald', 'Oswald'),
  ...sans('Source Sans 3', 'Source Sans 3'),
  ...sans('Work Sans', 'Work Sans'),
  ...sans('Mulish', 'Mulish'),
  ...sans('DM Sans', 'DM Sans'),
  ...sans('Manrope', 'Manrope'),
  ...sans('Outfit', 'Outfit'),
  ...sans('Plus Jakarta Sans', 'Plus Jakarta Sans'),
  ...sans('Space Grotesk', 'Space Grotesk'),
  ...sans('Karla', 'Karla'),
  ...sans('Quicksand', 'Quicksand'),
  ...sans('Barlow', 'Barlow'),

  // Serif
  ...serif('Playfair Display', 'Playfair Display'),
  ...serif('Merriweather', 'Merriweather'),
  ...serif('Lora', 'Lora'),
  ...serif('PT Serif', 'PT Serif'),
  ...serif('Source Serif 4', 'Source Serif 4'),
  ...serif('EB Garamond', 'EB Garamond'),
  ...serif('Cormorant Garamond', 'Cormorant Garamond'),
  ...serif('Crimson Pro', 'Crimson Pro'),
  ...serif('Spectral', 'Spectral'),
  ...serif('Libre Baskerville', 'Libre Baskerville'),
  ...serif('Bitter', 'Bitter'),
  ...serif('Cardo', 'Cardo'),

  // Display
  ...display('Bebas Neue', 'Bebas Neue'),
  ...display('Anton', 'Anton'),
  ...display('Archivo Black', 'Archivo Black'),
  ...display('Lobster', 'Lobster'),
  ...display('Pacifico', 'Pacifico'),
  ...display('Permanent Marker', 'Permanent Marker'),
  ...display('Righteous', 'Righteous'),
  ...display('Comfortaa', 'Comfortaa'),
  ...display('Fredoka', 'Fredoka'),
  ...display('Abril Fatface', 'Abril Fatface'),
  ...display('Patua One', 'Patua One'),
  ...display('Russo One', 'Russo One'),

  // Mono
  ...mono('Roboto Mono', 'Roboto Mono'),
  ...mono('Source Code Pro', 'Source Code Pro'),
  ...mono('JetBrains Mono', 'JetBrains Mono'),
  ...mono('Fira Code', 'Fira Code'),
  ...mono('Space Mono', 'Space Mono'),
  ...mono('IBM Plex Mono', 'IBM Plex Mono'),
  ...mono('Inconsolata', 'Inconsolata'),
  ...mono('Ubuntu Mono', 'Ubuntu Mono'),

  // Handwriting
  ...handwriting('Dancing Script', 'Dancing Script'),
  ...handwriting('Caveat', 'Caveat'),
  ...handwriting('Shadows Into Light', 'Shadows Into Light'),
  ...handwriting('Indie Flower', 'Indie Flower'),
  ...handwriting('Sacramento', 'Sacramento'),
  ...handwriting('Satisfy', 'Satisfy'),
  ...handwriting('Great Vibes', 'Great Vibes'),
  ...handwriting('Kalam', 'Kalam'),
];

// Tiny helpers keep the catalog declarations terse.
function sans(value: string, googleFamily: string): FontEntry[] {
  return [
    {
      value,
      label: value,
      preview: `"${value}", system-ui, sans-serif`,
      category: 'sans',
      googleFamily,
    },
  ];
}
function serif(value: string, googleFamily: string): FontEntry[] {
  return [
    {
      value,
      label: value,
      preview: `"${value}", Georgia, serif`,
      category: 'serif',
      googleFamily,
    },
  ];
}
function display(value: string, googleFamily: string): FontEntry[] {
  return [
    {
      value,
      label: value,
      preview: `"${value}", system-ui, sans-serif`,
      category: 'display',
      googleFamily,
    },
  ];
}
function mono(value: string, googleFamily: string): FontEntry[] {
  return [
    {
      value,
      label: value,
      preview: `"${value}", "Courier New", monospace`,
      category: 'mono',
      googleFamily,
    },
  ];
}
function handwriting(value: string, googleFamily: string): FontEntry[] {
  return [
    {
      value,
      label: value,
      preview: `"${value}", cursive`,
      category: 'handwriting',
      googleFamily,
    },
  ];
}

/** Filter the catalog by query + category. Both filters are
 *  case-insensitive substring matches against `label` (query) and
 *  exact match against `category`. `category: 'all'` skips the
 *  category filter. Empty query returns all entries (within the
 *  category filter). */
export function filterFonts(
  catalog: readonly FontEntry[],
  query: string,
  category: FontCategory | 'all',
): FontEntry[] {
  const q = query.trim().toLowerCase();
  return catalog.filter((entry) => {
    if (category !== 'all' && entry.category !== category) return false;
    if (q && !entry.label.toLowerCase().includes(q)) return false;
    return true;
  });
}

/** Returns the canonical entry for a `fontFamily` value, or
 *  undefined if not in the catalog. Used by `TextPanel` to render
 *  the current selection in its own face in the trigger. */
export function getFontEntry(value: string): FontEntry | undefined {
  return FONT_CATALOG.find((entry) => entry.value === value);
}

/** Build the Google Fonts CSS URL for a given family. Public so
 *  tests can pin the expected URL shape. */
export function googleFontsUrlFor(googleFamily: string): string {
  const encoded = encodeURIComponent(googleFamily).replace(/%20/g, '+');
  // Ship two weights (400 regular + 700 bold) and the italic axis
  // so Fabric IText's bold/italic toggles render correctly without
  // a second round-trip. `display=swap` keeps the fallback visible
  // until the file lands.
  return `https://fonts.googleapis.com/css2?family=${encoded}:ital,wght@0,400;0,700;1,400;1,700&display=swap`;
}

/** Per-family link tracker — keys are the canonical family name so
 *  repeated selection of the same font doesn't add duplicate links
 *  to <head>. Each entry is a Promise that resolves when the
 *  stylesheet `<link>` has finished loading (so `@font-face`
 *  declarations are parsed and registered with `document.fonts`).
 *  Subsequent callers reuse the same Promise rather than racing. */
const LOADED_FAMILIES = new Map<string, Promise<void>>();

/** Inject Google Fonts CSS for a family if not already injected.
 *  No-ops for catalog entries without a `googleFamily` (system
 *  fonts) and for repeat calls.
 *
 *  Side-effect-only — the caller updates `TextSettings.fontFamily`
 *  separately; the @font-face will swap in once loaded.
 *
 *  Safe to call from any context: SSR / Node / happy-dom branches
 *  with no `document` fall through silently (the test/server-side
 *  branches don't need the font face). */
export function ensureGoogleFontLoaded(fontFamilyValue: string): void {
  ensureGoogleFontStylesheet(fontFamilyValue);
}

/** Internal: inject the `<link>` and return a Promise that resolves
 *  when the stylesheet has finished loading (so `@font-face`
 *  declarations are registered with `document.fonts` before any
 *  consumer calls `document.fonts.load`). System fonts and unknown
 *  entries return an immediately-resolved Promise.
 *
 *  Memoized via `LOADED_FAMILIES` so repeat calls share one Promise
 *  and never re-inject the link. */
function ensureGoogleFontStylesheet(fontFamilyValue: string): Promise<void> {
  const entry = getFontEntry(fontFamilyValue);
  if (!entry || !entry.googleFamily) return Promise.resolve();
  const existing = LOADED_FAMILIES.get(entry.value);
  if (existing) return existing;
  if (typeof document === 'undefined') return Promise.resolve();
  const url = googleFontsUrlFor(entry.googleFamily);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.setAttribute('data-image-editor-google-font', entry.value);
  const ready = new Promise<void>((resolve) => {
    // Both onload and onerror resolve — we don't want a single
    // failed fetch to leave callers awaiting forever. The downstream
    // `document.fonts.load` is itself defensive and falls back to
    // the system font if our family never arrives.
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
  });
  document.head.appendChild(link);
  LOADED_FAMILIES.set(entry.value, ready);
  return ready;
}

/** How long to wait for a Google Fonts fetch before falling back
 *  to the system font. Some corporate firewalls, ad-blockers, and
 *  offline environments block fonts.googleapis.com silently —
 *  neither the `<link>` `load` nor `error` event fires. Without
 *  a timeout, `preloadFontsFromJSON`'s `Promise.all` would hang
 *  the entire `loadFromJSON` flow indefinitely. 3s is generous
 *  for a modern network and short enough that an unreachable CDN
 *  doesn't visibly stall session restore.
 *
 *  On timeout the caller sees the same resolution shape as
 *  success — no rejection — so downstream logic continues with
 *  whatever font is currently resolvable (fallback). */
export const FONT_READY_TIMEOUT_MS = 3000;

/** Returns a Promise that resolves once the named font is ready
 *  to render on a canvas2d context (i.e. the @font-face byte fetch
 *  has completed). Used by the post-`loadFromJSON` rehydration walk
 *  in `useFabricCanvas` to re-init IText dimensions + re-render
 *  after the saved fonts settle — without it, the canvas paints
 *  once with a fallback font (resolves before bytes are in) and
 *  then never re-paints when the bytes land.
 *
 *  Resolves immediately for system fonts (already available) and
 *  in non-browser test environments (no `document.fonts`). Catches
 *  rejections from `document.fonts.load` so a missing / blocked
 *  Google Fonts fetch doesn't poison the caller. Bounded by
 *  `FONT_READY_TIMEOUT_MS` so a blocked CDN can't hang the load. */
export async function whenFontReady(fontFamilyValue: string, sizePx: number = 16): Promise<void> {
  if (typeof document === 'undefined') return;
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts || typeof fonts.load !== 'function') return;
  // STEP 1 — wait for the `<link>` stylesheet to finish fetching.
  // Without this, the `@font-face` declarations may not be
  // registered with `document.fonts` yet, and `document.fonts.load`
  // would resolve immediately with [] (no FontFace matches found)
  // rather than waiting for the bytes. This was the subtle bug
  // where IText rendered against fallback metrics on first load
  // even after the awaited `whenFontReady` "resolved".
  //
  // STEP 2 — once the @font-face decls are registered,
  // `document.fonts.load` resolves with the matched FontFace
  // entries when their bytes land.
  //
  // Both steps race against a single timeout so a blocked CDN
  // doesn't hang the awaited result. The timeout NEVER rejects —
  // both paths resolve so callers can `await` without try/catch.
  await raceWithTimeout(
    (async () => {
      await ensureGoogleFontStylesheet(fontFamilyValue);
      try {
        await fonts.load(`${sizePx}px "${fontFamilyValue}"`);
      } catch {
        // Silent — fallback font keeps rendering; not a fatal error.
      }
    })(),
    FONT_READY_TIMEOUT_MS,
  );
}

/** Resolve when `promise` settles OR `ms` elapses, whichever first.
 *  Never rejects. Internal helper; not exported for now since
 *  there's only one caller. */
function raceWithTimeout(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const timer = setTimeout(done, ms);
    promise.finally(() => {
      clearTimeout(timer);
      done();
    });
  });
}

/** Reset the in-memory tracker. Test-only — production has no need
 *  to re-inject a removed link. */
export function _resetLoadedGoogleFontsForTests(): void {
  LOADED_FAMILIES.clear();
}

/** Test-only helper: returns true iff a link has been queued for
 *  the given family. Used by tests that don't want to await the
 *  load Promise. */
export function _hasLoadedGoogleFontLinkForTests(value: string): boolean {
  return LOADED_FAMILIES.has(value);
}

/** localStorage key for the per-user recent fonts list. Scoped to
 *  the arche so cross-arche conflicts aren't possible. */
export const RECENT_FONTS_STORAGE_KEY = 'image-editor:recent-fonts';

/** Max recents shown in the picker. Mirrors the recent-colors cap. */
export const RECENT_FONTS_MAX = 8;

/** Load recent fonts from localStorage. Returns an empty array on
 *  any parse/storage error so the picker is resilient to a corrupt
 *  entry. */
export function loadRecentFonts(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_FONTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, RECENT_FONTS_MAX);
  } catch {
    return [];
  }
}

/** Push a font value onto the recents list and persist. Dedupes by
 *  bringing the value to the front; caps at `RECENT_FONTS_MAX`.
 *  Returns the new list (so consumers can avoid a second read). */
export function recordRecentFont(value: string): string[] {
  const current = loadRecentFonts();
  const filtered = current.filter((v) => v !== value);
  const next = [value, ...filtered].slice(0, RECENT_FONTS_MAX);
  if (typeof window === 'undefined') return next;
  try {
    window.localStorage.setItem(RECENT_FONTS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage quota / private mode — silently fall back to the
    // in-memory list. Picker still works for the session.
  }
  return next;
}
